<template>
  <b-modal 
    id="modal-course-detail-completion" 
    centered 
    hide-footer 
    title="Course Detail"
    size="xl"
    body-class="p-0">
    <div class="modal-body-custom">
      <!-- Alert Info -->
      <div class="alert mb-0 rounded-0 border-0 d-flex align-items-center justify-content-between" :style="getAlertStyle(courseData?.status)">
        <div>
          <i :class="getAlertIcon(courseData?.status)" class="me-2" style="font-size: 1.2rem;"></i>
          <span class="text-dark">{{ getAlertMessage(courseData?.status) }}</span>
        </div>
        <!-- Action Buttons (Top Right) -->
        <div class="d-flex justify-content-end gap-2">
          <!-- Upload Certificate Button (for external and enrolled/rejected) -->
          <button 
            v-if="courseData?.training_type === 'external' && (courseData?.status === 'enrolled' || courseData?.status === 'rejected')"
            class="btn btn-sm btn-warning" 
            @click="uploadCertificate">
            <i class="fe fe-upload me-1"></i>
            Upload Certificate
          </button>
          <!-- Download Certificate Button (for completed) -->
          <button 
            v-if="courseData?.status === 'completed' && courseData?.certificate_path"
            class="btn btn-sm btn-success" 
            @click="downloadCertificate">
            <i class="fe fe-download me-1"></i>
            Download Certificate
          </button>
        </div>
      </div>

      <!-- Course Content -->
      <div class="row g-0">
        <!-- Left Side - Course Info -->
        <div class="col-md-12 p-4 border-end">
          <!-- Schedule Badge -->
          <div class="row">
            <div class="col-md-7" style="display: flex; flex-direction: column; justify-content: space-between;">
              <!-- Status Badge -->
              <div class="mb-3">
                <span :class="getStatusBadgeClass(courseData?.status)" style="padding: 0.35rem 0.75rem;">
                  <i class="fe fe-info me-1"></i>
                  {{ formatStatus(courseData?.tna_status) }}
                </span>
                <span :class="getTypeBadgeClass(courseData?.training_type)" class="ms-2" style="padding: 0.35rem 0.75rem;">
                  {{ formatType(courseData?.training_type) }}
                </span>
              </div>

              <!-- Course Title -->
              <h3 class="mb-3 font-weight-bold" style="color: #1a202c;">
                {{ courseData?.title || '-' }}
              </h3>

              <!-- Category Badges -->
              <div class="mb-4 d-flex gap-2">
                <span class="badge-icon bg-danger-soft text-danger">
                  <i :class="getTrainingTypeIcon(courseData?.training_type)"></i>
                </span>
                <span class="badge-custom bg-warning-soft text-warning">
                  {{ courseData?.role_competency || '-' }}
                </span>
                <span class="badge-custom bg-info-soft text-info">
                  {{ courseData?.role_competency_type || '-' }}
                </span>
              </div>

              <!-- Stats -->
              <div class="row mb-4 p-3 rounded" style="background-color: #FAFAFA;">
                <div class="col-4">
                  <div class="d-flex align-items-center text-muted">
                    <i class="fe fe-clock me-2" style="font-size: 1.1rem;"></i>
                    <div>
                      <small class="d-block" style="font-size: 0.7rem;">Learning Hours</small>
                      <strong class="text-dark">{{ courseData?.hour ? Number(courseData?.hour) : '-' }} hrs</strong>
                    </div>
                  </div>
                </div>
                <div class="col-4">
                  <div class="d-flex align-items-center text-muted">
                    <i class="fe fe-dollar-sign me-2" style="font-size: 1.1rem;"></i>
                    <div>
                      <small class="d-block" style="font-size: 0.7rem;">Estimated Price</small>
                      <strong class="text-dark">IDR {{ courseData?.closest_batch_total_price ? compactNumberFormatter(courseData?.closest_batch_total_price) : '-' }}</strong>
                    </div>
                  </div>
                </div>
                <div class="col-4">
                  <div class="d-flex align-items-center text-muted">
                    <i class="fe fe-users me-2" style="font-size: 1.1rem;"></i>
                    <div>
                      <small class="d-block" style="font-size: 0.7rem;">Enrolled</small>
                      <strong class="text-dark">{{ courseData?.closest_batch_participant_count || '-' }} / {{ courseData?.closest_batch_quota || '-' }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Right Side - Course Image -->
            <div class="col-md-5 p-0">
              <div class="course-image-container rounded">
                <img 
                  :src="courseData?.cover_image_path ? `${imageBaseUrl}/attachments/${courseData?.cover_image_path}` : 'https://dummyimage.com/300x200/FAFAFA/000000&text=Course'" 
                  alt="Course Thumbnail"
                  class="course-image"
                  @error="handleImageError">
              </div>
            </div>
          </div>

          <!-- Tabs -->
          <ul class="nav nav-tabs mb-3" role="tablist">
            <li class="nav-item">
              <a href="#" class="nav-link" :class="{ active: activeTab === 'description' }" @click.prevent="activeTab = 'description'">
                Description
              </a>
            </li>
            <li class="nav-item">
              <a href="#" class="nav-link" :class="{ active: activeTab === 'attributes' }" @click.prevent="activeTab = 'attributes'">
                Attributes
              </a>
            </li>
            <li class="nav-item">
              <a href="#" class="nav-link" :class="{ active: activeTab === 'platform' }" @click.prevent="activeTab = 'platform'">
                Platform
              </a>
            </li>
            <li class="nav-item">
              <a href="#" class="nav-link" :class="{ active: activeTab === 'schedule' }" @click.prevent="activeTab = 'schedule'">
                Schedule
              </a>
            </li>
            <li class="nav-item">
              <a href="#" class="nav-link" :class="{ active: activeTab === 'certificate' }" @click.prevent="activeTab = 'certificate'">
                Certificate
              </a>
            </li>
          </ul>

          <!-- Tab Content -->
          <div class="tab-content">
            <!-- Description Tab -->
            <div v-show="activeTab === 'description'" class="tab-pane fade show active" id="description-completion">
              <p class="mb-3" style="line-height: 1.8; font-size: 0.9rem;">
                {{ courseData?.description || 'No description available.' }}
              </p>
              
              <!-- Related Competencies -->
              <div class="mt-4" v-if="courseData?.competencies && courseData?.competencies.length > 0">
                <h6 class="font-weight-bold mb-3">Related Competencies</h6>
                <div class="d-flex gap-2 flex-wrap">
                  <span 
                    v-for="(comp, index) in courseData?.competencies" 
                    :key="index"
                    class="badge bg-white text-dark border px-3 py-2" 
                    style="font-weight: 500;">
                    {{ comp.competency_id }} <span class="text-muted">|</span> <i class="fe fe-star text-warning"></i> {{ comp.proficiency ? comp.proficiency : '-' }}
                  </span>
                </div>
                <p class="text-muted mt-3 mb-0" style="font-size: 0.75rem;">
                  **Notes: <i class="fe fe-star text-warning"></i> Levels of proficiency 1-5
                </p>
              </div>
            </div>

            <!-- Attributes Tab -->
            <div v-show="activeTab === 'attributes'" class="tab-pane fade show active" id="attributes-completion">
              <div class="row">
                <!-- Left Column -->
                <div class="col-md-6">
                  <!-- Course Method -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Course Method</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData?.batches && courseData?.batches[0]?.training_method || '-' }}</p>
                  </div>

                  <!-- Course Category -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Course Category</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData?.training_category || '-' }}</p>
                  </div>

                  <!-- Course URL -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Course URL</label>
                    <div class="d-flex align-items-center gap-2">
                      <a v-if="courseData?.website"
                         :href="courseData?.website" 
                         target="_blank" 
                         class="text-primary text-truncate" 
                         style="max-width: 80%; text-decoration: none;">
                        {{ courseData?.website }}
                      </a>
                      <span v-else class="text-muted">-</span>
                      <a v-if="courseData?.website"
                        :href="courseData?.website"
                        target="_blank"
                        class="btn btn-sm btn-danger d-flex align-items-center justify-content-center" 
                        style="width: 32px; height: 32px; padding: 0; border-radius: 4px;">
                        <i class="fe fe-send" style="font-size: 14px;"></i>
                      </a>
                    </div>
                  </div>

                  <!-- Department -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Department</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData?.department || '-' }}</p>
                  </div>

                  <!-- Division -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Division</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData?.division || '-' }}</p>
                  </div>
                </div>

                <!-- Right Column -->
                <div class="col-md-6">
                  <!-- Course Type -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Course Type</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData?.training_type_name || '-' }}</p>
                  </div>

                  <!-- Certificate Validity Period -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Certificate Validity Period (Months)</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData?.certificate_validity_period || '-' }}</p>
                  </div>

                  <!-- Course Scope -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Course Scope</label>
                    <div>
                      <span v-for="training_scope_name in courseData?.training_scope" :key="training_scope_name" class="badge badge-dark font-weight-bold mr-1">
                        {{ training_scope_name }}
                      </span>
                      <span v-if="!courseData?.training_scope || courseData?.training_scope.length === 0" class="text-muted">-</span>
                    </div>
                  </div>

                  <!-- STEM Category -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">STEM Category</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData?.stem_category || '-' }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Platform Tab -->
            <div v-show="activeTab === 'platform'" class="tab-pane fade show active" id="platform-completion">
              <div class="row">
                <!-- Left Column -->
                <div class="col-md-6">
                  <!-- Platform Name -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Platform Name</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData?.platform || '-' }}</p>
                  </div>

                  <!-- Training Provider (LDP) -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Training Provider</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData?.provider_name || '-' }}</p>
                  </div>

                  <!-- Provider Website -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Provider Website</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData?.provider_website || '-' }}</p>
                  </div>

                  <!-- Email -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Email</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData?.provider_email || '-' }}</p>
                  </div>
                </div>

                <!-- Right Column -->
                <div class="col-md-6">
                  <!-- Provider Name (LSP) -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Provider Name (LSP)</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData?.lsp_name || '-' }}</p>
                  </div>

                  <!-- Address -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Address</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData?.provider_address || '-' }}</p>
                  </div>

                  <!-- Contact Person -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Contact Person</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData?.provider_contact_person || '-' }}</p>
                  </div>

                  <!-- Phone -->
                  <div class="mb-4 py-2 px-3 rounded" style="background-color: #fafafa;">
                    <label class="text-muted small mb-2">Phone</label>
                    <p class="text-dark font-weight-bold mb-0">{{ courseData?.provider_phone || '-' }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Schedule Tab -->
            <div v-show="activeTab === 'schedule'" class="tab-pane fade show active" id="schedule-completion">

              <!-- Schedule Table -->
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead class="rounded" style="background-color: #f8f9fa;">
                    <tr>
                      <th class="text-muted small font-weight-normal">BATCH</th>
                      <th class="text-muted small font-weight-normal">QUOTA</th>
                      <th class="text-muted small font-weight-normal">USED QUOTA</th>
                      <th class="text-muted small font-weight-normal">REMAINING QUOTA</th>
                      <th class="text-muted small font-weight-normal">PENYELENGGARA</th>
                      <th class="text-muted small font-weight-normal">SCHEDULE</th>
                      <th class="text-muted small font-weight-normal">ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    <!-- Empty State -->
                    <tr v-if="!courseData || !courseData?.batches || courseData?.batches.length === 0">
                      <td colspan="7" class="text-center py-5">
                        <i class="fe fe-inbox text-muted" style="font-size: 3rem;"></i>
                        <p class="text-muted mt-2">No batches available</p>
                      </td>
                    </tr>
                    
                    <!-- Batch Rows -->
                    <tr v-for="(batch, index) in courseData?.batches || []" :key="batch.id || index">
                      <td class="align-middle">{{ formatBatchLabel(batch) }}</td>
                      <td class="align-middle">{{ batch.quota || '-' }}</td>
                      <td class="align-middle">{{ batch?.enrolled || 0 }}</td>
                      <td class="align-middle">{{ batch?.available_quota || '-' }}</td>
                      <td class="align-middle">{{ formatPenyelenggara(batch?.penyelenggara) }}</td>
                      <td class="align-middle">
                        <div v-if="batch.schedules && batch.schedules.length > 0">
                          <div v-for="(schedule, idx) in batch.schedules" :key="idx" :class="{ 'mt-2': idx > 0 }">
                            <strong class="d-block">{{ formatLongDate(schedule.date) || '-' }}</strong>
                            <small class="text-muted">{{ formatTime(schedule.start_time) || '-' }} - {{ formatTime(schedule.end_time) || '-' }}</small>
                          </div>
                        </div>
                        <span v-else class="text-muted">No schedule</span>
                      </td>
                      <td class="align-middle">
                        <div class="d-flex gap-2">
                          <button 
                            class="btn btn-sm btn-info d-flex align-items-center justify-content-center" 
                            style="width: 36px; height: 36px; padding: 0;"
                            @click="openParticipantModal(batch)">
                            <i class="fe fe-users" style="font-size: 16px;"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Pagination -->
              <div class="d-flex justify-content-between align-items-center mt-3" v-if="courseData?.batches && courseData?.batches.length > 0">
                <div>
                  <small class="text-muted">Showing {{ courseData?.batches?.length }} of {{ courseData?.batches?.length }}</small>
                </div>
                <nav aria-label="Schedule pagination">
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item disabled">
                      <button class="page-link" disabled>
                        <i class="fe fe-chevron-left"></i>
                      </button>
                    </li>
                    <li class="page-item active">
                      <button class="page-link">1</button>
                    </li>
                    <li class="page-item disabled">
                      <button class="page-link" disabled>
                        <i class="fe fe-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>

            <!-- Certificate Tab -->
            <div v-show="activeTab === 'certificate'" class="tab-pane fade show active" id="certificate-completion">
              <!-- Rejection Reason Alert -->
              <div v-if="courseData?.status === 'rejected' && courseData?.rejection_reason" class="alert alert-danger mb-4">
                <h6 class="alert-heading font-weight-bold">
                  <i class="fe fe-alert-circle me-2"></i>Certificate Rejected
                </h6>
                <p class="mb-0">{{ courseData?.rejection_reason }}</p>
              </div>

              <!-- Certificate Section -->
              <div class="row">
                <div class="col-md-8">
                  <!-- No Certificate -->
                  <div v-if="!courseData?.certificate_path && courseData?.status !== 'completed'" class="text-center py-5">
                    <i class="fe fe-file-text text-muted" style="font-size: 4rem;"></i>
                    <p class="text-muted mt-3 mb-3">No certificate uploaded yet</p>
                    <button 
                      v-if="courseData?.training_type === 'external'"
                      class="btn btn-warning"
                      @click="uploadCertificate">
                      <i class="fe fe-upload me-1"></i>
                      Upload Certificate
                    </button>
                  </div>

                  <!-- Has Certificate -->
                  <div v-else-if="courseData?.certificate_path">
                    <!-- PDF Certificate - Same style as course image -->
                    <div v-if="isPdfCertificate(courseData?.certificate_path)" 
                         class="course-image-container rounded" 
                         style="cursor: pointer;"
                         @click="previewCertificate">
                      <div class="pdf-overlay">
                        <div class="pdf-icon-container-large">
                          <i class="fe fe-file-text"></i>
                        </div>
                        <p class="pdf-label mt-3 mb-1">PDF Certificate</p>
                        <small class="text-muted">Click to view</small>
                      </div>
                    </div>
                    <!-- Image Certificate - Same style as course image -->
                    <div v-else class="course-image-container rounded" style="cursor: pointer;" @click="previewCertificate">
                      <img 
                        :src="`${imageBaseUrl}/attachments/${courseData?.certificate_path}`" 
                        alt="Certificate"
                        class="course-image"
                        @error="handleCertImageError">
                    </div>
                    
                    <!-- Action Buttons -->
                    <div class="d-flex justify-content-center gap-2 mt-3">
                      <button 
                        v-if="courseData?.status === 'completed'"
                        class="btn btn-success"
                        @click="downloadCertificate">
                        <i class="fe fe-download me-1"></i>
                        Download
                      </button>
                      <button 
                        v-if="courseData?.training_type === 'external' && (courseData?.status === 'enrolled' || courseData?.status === 'rejected')"
                        class="btn btn-warning"
                        @click="uploadCertificate">
                        <i class="fe fe-upload me-1"></i>
                        Re-upload
                      </button>
                    </div>
                  </div>

                  <!-- Completed but no certificate path (for internal courses) -->
                  <div v-else-if="courseData?.status === 'completed' && !courseData?.certificate_path" class="text-center py-5">
                    <i class="fe fe-check-circle text-success" style="font-size: 4rem;"></i>
                    <p class="text-success mt-3 mb-0 font-weight-bold">Course Completed</p>
                    <p class="text-muted small">Certificate will be generated automatically</p>
                  </div>
                </div>

                <!-- <div class="col-md-4">
                  <div class="info-section p-4 rounded border" style="background-color: #fafafa;">
                    <h6 class="font-weight-bold mb-3">
                      <i class="fe fe-info me-2"></i>Certificate Information
                    </h6>
                    <div class="mb-3">
                      <label class="text-muted small mb-1">Certificate Validity Period</label>
                      <p class="text-dark font-weight-bold mb-0">{{ courseData?.certificate_validity_period || '-' }} Months</p>
                    </div>
                    <div class="mb-3">
                      <label class="text-muted small mb-1">Completion Date</label>
                      <p class="text-dark font-weight-bold mb-0">{{ courseData?.completion_date ? formatDate(courseData?.completion_date) : '-' }}</p>
                    </div>
                    <div class="mb-3">
                      <label class="text-muted small mb-1">Training Method</label>
                      <p class="text-dark font-weight-bold mb-0">{{ courseData?.training_method_name || courseData?.training_method || '-' }}</p>
                    </div>
                    <div class="mb-3">
                      <label class="text-muted small mb-1">Training Type</label>
                      <p class="text-dark font-weight-bold mb-0">{{ formatType(courseData?.training_type) }}</p>
                    </div>
                    <div class="mb-3">
                      <label class="text-muted small mb-1">Enrollment Status</label>
                      <span :class="getStatusBadgeClass(courseData?.status)">
                        {{ formatStatus(courseData?.status) }}
                      </span>
                    </div>
                  </div>
                </div> -->
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Participant Modal - Ugems University -->
    <modal-participant-ugems
      v-if="courseData?.platform?.toLowerCase() === 'ugems university'"
      :batchId="selectedBatchId"
      :course-title="courseData?.title"
      :batch-schedules="selectedBatch?.schedules"
      :training_type="courseData?.training_type"
      :platform="courseData?.platform"
      @upload-certificate="openUploadCompletionModal"
      @view-completion="openCompletionDetailModal"
      @hidden="resetParticipantModal"
    />
    
    <!-- Participant Modal - Other Platforms -->
    <modal-participant
      v-else
      :batchId="selectedBatchId"
      :course-title="courseData?.title"
      :batch-schedules="selectedBatch?.schedules"
      @upload-certificate="openUploadCompletionModal"
      @view-completion="openCompletionDetailModal"
      @hidden="resetParticipantModal"
    />
    
    <!-- Upload Completion Modal -->
    <modal-upload-completion 
      :selectedParticipant="selectedParticipant"
      @completion-submitted="handleCompletionSubmitted"
    />
    
    <!-- Completion Detail Modal (User View) -->
    <modal-completion-detail 
      :completionId="selectedCompletionId"
      @reply-submitted="handleReplySubmitted"
    />
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ModalParticipant from './modal-participant.vue';
import ModalParticipantUgems from './modal-participant-ugems.vue';
import ModalUploadCompletion from './modal-upload-completion.vue';
import ModalCompletionDetail from './modal-completion-detail.vue';
import { compactNumberFormatter } from '@/utils/currencyFormatter';
import { formatLongDate, formatTime, formatDate } from '@/utils/dateFormatter';
import { formatBatchLabel } from '@/utils/batchIdentity';

export default {
  name: 'ModalCourseDetailCompletion',
  components: {
    ModalParticipant,
    ModalParticipantUgems,
    ModalUploadCompletion,
    ModalCompletionDetail
  },
  props: {
    selectedCourse: {
      type: Object,
      default: null
    }
  },
  computed: {
    ...mapGetters('courseCompletionMitra', {
      courseDetail: 'courseDetail',
      isLoading: 'loading'
    }),
    courseData() {
      return this.courseDetail || this.selectedCourse;
    }
  },
  data() {
    return {
      imageBaseUrl: process.env.VUE_APP_API_URL_UGEMS_UNIV,
      selectedYear: null,
      participants: null,
      selectedBatchId: null,
      selectedBatch: null,
      selectedParticipant: null,
      selectedCompletionId: null,
      activeTab: 'schedule',
      yearOptions: [
        { label: '2023', value: 2023 },
        { label: '2024', value: 2024 },
        { label: '2025', value: 2025 },
        { label: '2026', value: 2026 },
        { label: '2027', value: 2027 }
      ]
    }
  },
  watch: {
    selectedCourse: {
      immediate: true,
      handler(newCourse) {
        if (newCourse && newCourse.id) {
          this.activeTab = 'schedule';
          this.loadCourseDetail(newCourse.id);
        }
      }
    }
  },
  methods: {
    ...mapActions('courseCompletionMitra', ['getCourseDetail']),
    compactNumberFormatter,
    formatLongDate,
    formatTime,
    formatDate,
    formatBatchLabel,

    async loadCourseDetail(courseId) {
      try {
        await this.getCourseDetail(courseId);
      } catch (error) {
        console.error('Error loading course detail:', error);
        this.$swal('Gagal Memuat Data', 'Detail pelatihan belum berhasil dimuat.', 'error');
      }
    },

    handleImageError(event) {
      event.target.src = 'https://dummyimage.com/300x200/FAFAFA/000000&text=Course';
    },

    handleCertImageError(event) {
      event.target.src = 'https://dummyimage.com/300x200/FAFAFA/000000&text=Certificate';
    },

    isPdfCertificate(certificatePath) {
      if (!certificatePath) return false;
      const lowerPath = certificatePath.toLowerCase();
      return lowerPath.endsWith('.pdf');
    },

    previewCertificate() {
      if (this.courseData?.certificate_path) {
        const certUrl = `${this.imageBaseUrl}/attachments/${this.courseData.certificate_path}`;
        window.open(certUrl, '_blank');
      }
    },

    formatStatus(status) {
      const statusMap = {
        'enrolled': 'Enrolled',
        'in_review': 'In Review',
        'completed': 'Completed',
        'rejected': 'Rejected'
      };
      return statusMap[status] || status || '-';
    },

    formatType(type) {
      const typeMap = {
        'internal': 'Internal',
        'external': 'External',
        'external_lms': 'External LMS'
      };
      return typeMap[type] || type || '-';
    },

    formatBatchStatus(status) {
      if (!status) return '';
      return status
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    },

    formatPenyelenggara(value) {
      if (!value) return '-';
      return String(value)
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },

    getStatusBadgeClass(status) {
      const classes = {
        'enrolled': 'badge bg-primary',
        'in_review': 'badge bg-warning',
        'completed': 'badge bg-success',
        'rejected': 'badge bg-danger'
      };
      return classes[status] || 'badge bg-secondary';
    },

    getTypeBadgeClass(type) {
      const classes = {
        'internal': 'badge bg-info',
        'external': 'badge bg-purple',
        'external_lms': 'badge bg-teal'
      };
      return classes[type] || 'badge bg-secondary';
    },

    getBatchStatusBadgeClass(status) {
      if (!status) return 'badge badge-pill px-3 py-2';
      
      const normalizedStatus = status.toLowerCase();
      
      if (normalizedStatus === 'upcoming') {
        return 'badge badge-pill px-3 py-2 badge-warning';
      } else if (normalizedStatus === 'in_progress') {
        return 'badge badge-pill px-3 py-2 badge-info';
      } else if (normalizedStatus === 'completed') {
        return 'badge badge-pill px-3 py-2 badge-success';
      }
      
      return 'badge badge-pill px-3 py-2 badge-secondary';
    },

    getAlertStyle(status) {
      const styles = {
        'enrolled': 'background-color: #e3f2fd;',
        'in_review': 'background-color: #fff3e0;',
        'completed': 'background-color: #e8f5e9;',
        'rejected': 'background-color: #ffebee;'
      };
      return styles[status] || 'background-color: #fafafa;';
    },

    getAlertIcon(status) {
      const icons = {
        'enrolled': 'fe fe-book-open text-primary',
        'in_review': 'fe fe-clock text-warning',
        'completed': 'fe fe-check-circle text-success',
        'rejected': 'fe fe-x-circle text-danger'
      };
      return icons[status] || 'fe fe-info text-secondary';
    },

    getAlertMessage(status) {
      const messages = {
        'enrolled': 'Anda terdaftar pada pelatihan ini. Selesaikan pelatihan dan unggah sertifikat Anda.',
        'in_review': 'Sertifikat Anda sedang direview oleh tim HSE Training.',
        'completed': 'Selamat! Anda telah menyelesaikan pelatihan ini.',
        'rejected': 'Sertifikat Anda ditolak. Unggah sertifikat baru.'
      };
      return messages[status] || 'Informasi pelatihan';
    },

    getTrainingTypeIcon(trainingType) {
      const type = trainingType?.toLowerCase() || '';
      
      if (type === 'internal') {
        return 'fe fe-home';
      } else if (type === 'external') {
        return 'fe fe-globe';
      } else if (type === 'external_lms') {
        return 'fe fe-monitor';
      }
      return 'fe fe-book-open';
    },

    uploadCertificate() {
      this.$bvModal.hide('modal-course-detail-completion');
      this.$emit('upload-certificate', this.courseData);
    },

    downloadCertificate() {
      this.$emit('download-certificate', this.courseData);
    },

    openParticipantModal(batch) {
      // Pass batch ID for both platforms
      this.selectedBatchId = batch.id || batch.training_event_id;
      this.selectedBatch = batch || null;
      this.$bvModal.show('modal-participant-completion');
    },
    
    openUploadCompletionModal(participant) {
      this.selectedParticipant = participant;
      this.$bvModal.hide('modal-participant-completion');
      this.$bvModal.show('modal-upload-completion');
    },
    
    handleCompletionSubmitted() {
      // Reload course detail to refresh participant data
      if (this.selectedCourse && this.selectedCourse.id) {
        this.loadCourseDetail(this.selectedCourse.id);
      }
      this.$bvModal.hide('modal-upload-completion');
      this.$swal({
        title: 'Success',
        text: 'Sertifikat berhasil dikirim',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
    },
    
    openCompletionDetailModal(participant) {
      // Use completion_id or id from participant
      this.selectedCompletionId = participant.completion_id || participant.id;
      this.$nextTick(() => {
        this.$bvModal.show('modal-completion-detail-user');
      });
    },
    
    handleReplySubmitted() {
      // Reload course detail to refresh participant data
      if (this.selectedCourse && this.selectedCourse.id) {
        this.loadCourseDetail(this.selectedCourse.id);
      }
    },

    resetParticipantModal() {
      // Reset state when participant modal closes to prevent stale API calls
      this.selectedBatchId = null;
      this.selectedBatch = null;
    }
  }
}
</script>

<style scoped>
.modal-body-custom {
  padding: 0;
}

/* Badge Styles */
.badge-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 50%;
  font-size: 0.75rem;
}

.badge-custom {
  padding: 0.35rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 0.35rem;
}

/* Soft Badge Colors */
.bg-danger-soft {
  background-color: #fce4e4;
}

.text-danger {
  color: #e63757 !important;
}

.bg-warning-soft {
  background-color: #fef5e4;
}

.text-warning {
  color: #f6c343 !important;
}

.bg-info-soft {
  background-color: #e4f4f7;
}

.text-info {
  color: #39afd1 !important;
}

.bg-primary {
  background-color: #2c7be5 !important;
}

.bg-success {
  background-color: #00d97e !important;
}

.bg-warning {
  background-color: #f6c343 !important;
}

.bg-danger {
  background-color: #e63757 !important;
}

.bg-purple {
  background-color: #6b5eae !important;
}

.bg-teal {
  background-color: #00d4b4 !important;
}

/* Tab Styles */
.nav-tabs {
  border-bottom: 1px solid #dee2e6;
}

.nav-tabs .nav-link {
  color: #718096;
  font-weight: 500;
  border: none !important;
  border-bottom: 3px solid transparent !important;
  padding: 0.75rem 1rem;
  background: transparent !important;
}

.nav-tabs .nav-link:hover {
  border-color: transparent transparent #dee2e6 transparent !important;
  color: #4a5568;
}

.nav-tabs .nav-link.active {
  color: #e63757 !important;
  font-weight: 600;
  border-bottom: 3px solid #e63757 !important;
  background: transparent !important;
}

.tab-content {
  padding-top: 1rem;
}

/* Course Image Landscape Container */
.course-image-container {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  overflow: hidden;
  background-color: #fafafa;
}

.course-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* PDF Overlay for course-image-container */
.pdf-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fafafa;
}

/* Schedule Table Styles */
.table {
  margin-bottom: 0;
}

.table thead th {
  border-bottom: 2px solid #dee2e6;
  padding: 0.75rem 1rem;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

.table tbody td {
  padding: 1rem;
  vertical-align: middle;
  border-bottom: 1px solid #f1f1f1;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

/* Status Badge Styles */
.badge-warning {
  background-color: #fef5e4 !important;
  color: #f6c343 !important;
  font-weight: 500;
}

.badge-info {
  background-color: #e4f4f7 !important;
  color: #39afd1 !important;
  font-weight: 500;
}

.badge-success {
  background-color: #d4edda !important;
  color: #00d97e !important;
  font-weight: 500;
}

.badge-secondary {
  background-color: #e9ecef !important;
  color: #6c757d !important;
  font-weight: 500;
}

/* Pagination Styles */
.pagination {
  gap: 0.25rem;
}

.pagination .page-link {
  color: #2c7be5;
  border: 1px solid #dee2e6;
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem;
  min-width: 36px;
  text-align: center;
}

.pagination .page-item.active .page-link {
  background-color: #e63757;
  border-color: #e63757;
  color: white;
}

.pagination .page-item.disabled .page-link {
  cursor: not-allowed;
  opacity: 0.5;
  background-color: transparent;
}

.pagination .page-link:hover:not(.disabled) {
  background-color: #f8f9fa;
}

.gap-2 {
  gap: 0.5rem;
}

.me-1 {
  margin-right: 0.25rem;
}

.me-2 {
  margin-right: 0.5rem;
}

.ms-2 {
  margin-left: 0.5rem;
}

.mr-1 {
  margin-right: 0.25rem;
}

/* Certificate Tab Styles */
.certificate-section,
.info-section {
  height: 100%;
}

.certificate-preview {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid #dee2e6;
}

.certificate-preview:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Certificate Full Image Styles */
.certificate-full-container {
  width: 100%;
  text-align: center;
}

.certificate-full-image {
  max-width: 100%;
  /* max-height: 500px; */
  object-fit: contain;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.certificate-full-image:hover {
  transform: scale(1.01);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

/* PDF Full Thumbnail Styles */
.pdf-thumbnail-full {
  cursor: pointer;
  background-color: #fff;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.pdf-thumbnail-full:hover {
  border-color: #e63757;
  background-color: #fef5f7;
}

.pdf-icon-container-large {
  width: 100px;
  height: 130px;
  margin: 0 auto;
  background: linear-gradient(135deg, #e63757 0%, #c82333 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(230, 55, 87, 0.3);
}

.pdf-icon-container-large i {
  font-size: 3.5rem;
  color: #fff;
}

.alert-heading {
  margin-bottom: 0.5rem;
}

/* PDF Thumbnail Styles */
.pdf-thumbnail {
  cursor: pointer;
  padding: 2rem;
  background-color: #fff;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.pdf-thumbnail:hover {
  border-color: #e63757;
  background-color: #fef5f7;
}

.pdf-icon-container {
  width: 80px;
  height: 100px;
  margin: 0 auto 1rem;
  background: linear-gradient(135deg, #e63757 0%, #c82333 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(230, 55, 87, 0.3);
}

.pdf-icon-container i {
  font-size: 2.5rem;
  color: #fff;
}

.pdf-label {
  font-weight: 600;
  color: #1a202c;
  font-size: 1rem;
}
</style>
